import React from "react";
import { Link } from "react-router-dom";
import { useLanguage, useTheme } from "../App";
import { Sun, Moon, Globe, ArrowLeft } from "lucide-react";

const DataProcessingAgreement = () => {
  const { language, changeLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_saloneos-booking/artifacts/eysotq4t_smartbooker%20logo.png" 
                alt="SmartBooker" 
                className="h-8 w-auto"
              />
            </Link>

            <div className="flex items-center gap-4">
              <button
                onClick={() => changeLanguage(language === "it" ? "en" : "it")}
                className="p-2 rounded-full hover:bg-muted transition-colors"
              >
                <Globe className="w-5 h-5" />
              </button>
              
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full hover:bg-muted transition-colors"
              >
                {theme === "light" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            {language === "it" ? "Torna alla home" : "Back to home"}
          </Link>

          <div className="prose prose-slate dark:prose-invert max-w-none">
            <h1 className="text-3xl font-serif font-medium mb-2">Data Processing Agreement (DPA) – SmartBooker</h1>
            <p className="text-muted-foreground mb-8">Ultimo aggiornamento: 13/03/2026</p>

            <p>
              Il presente Data Processing Agreement (DPA) disciplina il trattamento dei dati personali effettuato 
              da SmartBooker per conto dei saloni che utilizzano la piattaforma.
            </p>
            
            <p>Il presente accordo è parte integrante dei Termini e Condizioni di SmartBooker.</p>
            
            <p className="font-medium">
              Il trattamento dei dati avviene nel rispetto del Regolamento generale sulla protezione dei dati (GDPR).
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">1. Definizioni</h2>
            <p>Ai fini del presente accordo si intende per:</p>
            <ul className="list-none pl-0 space-y-3 mt-4">
              <li>
                <strong>Titolare del trattamento:</strong><br />
                il salone o l'azienda che utilizza SmartBooker per gestire prenotazioni e clienti.
              </li>
              <li>
                <strong>Responsabile del trattamento:</strong><br />
                SmartBooker, che fornisce la piattaforma tecnologica.
              </li>
              <li>
                <strong>Dati personali:</strong><br />
                qualsiasi informazione relativa a una persona fisica identificata o identificabile.
              </li>
              <li>
                <strong>Trattamento:</strong><br />
                qualsiasi operazione effettuata sui dati personali.
              </li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">2. Oggetto del trattamento</h2>
            <p>
              SmartBooker tratta dati personali esclusivamente per fornire il servizio di gestione prenotazioni 
              e appuntamenti tramite la propria piattaforma SaaS.
            </p>
            <p className="mt-4">Il trattamento include:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>archiviazione dei dati dei clienti del salone</li>
              <li>gestione delle prenotazioni</li>
              <li>invio di notifiche e promemoria appuntamenti</li>
              <li>gestione degli account utenti</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">3. Tipologia di dati trattati</h2>
            <p>I dati personali trattati possono includere:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>nome e cognome</li>
              <li>indirizzo email</li>
              <li>numero di telefono</li>
              <li>informazioni sugli appuntamenti</li>
              <li>servizi prenotati</li>
              <li>dati tecnici di utilizzo della piattaforma</li>
            </ul>
            <p className="mt-4 font-medium">
              SmartBooker non tratta intenzionalmente categorie particolari di dati personali.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">4. Finalità del trattamento</h2>
            <p>Il trattamento dei dati viene effettuato esclusivamente per:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>consentire il funzionamento della piattaforma SmartBooker</li>
              <li>gestire prenotazioni e appuntamenti</li>
              <li>inviare notifiche relative agli appuntamenti</li>
              <li>garantire sicurezza e stabilità del servizio</li>
            </ul>
            <p className="mt-4 font-medium">
              SmartBooker non utilizza i dati per finalità proprie o di marketing.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">5. Obblighi del responsabile del trattamento</h2>
            <p>SmartBooker si impegna a:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>trattare i dati solo su istruzione documentata del titolare</li>
              <li>garantire la riservatezza dei dati trattati</li>
              <li>adottare misure tecniche e organizzative adeguate per proteggere i dati</li>
              <li>assistere il titolare nel rispetto degli obblighi previsti dal GDPR</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">6. Sicurezza dei dati</h2>
            <p>SmartBooker implementa misure di sicurezza adeguate per proteggere i dati personali, tra cui:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>connessioni sicure HTTPS</li>
              <li>sistemi di autenticazione e controllo accessi</li>
              <li>backup periodici dei dati</li>
              <li>monitoraggio dei sistemi</li>
              <li>protezione contro accessi non autorizzati</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">7. Sub-responsabili del trattamento</h2>
            <p>
              SmartBooker può utilizzare fornitori terzi per fornire alcuni servizi tecnici necessari 
              al funzionamento della piattaforma.
            </p>
            <p className="mt-4">Questi fornitori possono includere:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>servizi di hosting cloud</li>
              <li>provider email e notifiche</li>
              <li>servizi di pagamento</li>
              <li>strumenti di sicurezza e monitoraggio</li>
            </ul>
            <p className="mt-4">
              Tali soggetti operano come sub-responsabili del trattamento e sono vincolati da obblighi 
              di protezione dei dati conformi al Regolamento generale sulla protezione dei dati.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">8. Trasferimento dei dati</h2>
            <p>I dati personali possono essere trattati su server situati all'interno dell'Unione Europea.</p>
            <p className="mt-4">
              Qualora alcuni fornitori utilizzino server situati al di fuori dell'UE, il trasferimento 
              avverrà nel rispetto delle garanzie previste dal GDPR.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">9. Diritti degli interessati</h2>
            <p>SmartBooker assiste il titolare del trattamento nel rispondere alle richieste degli interessati relative a:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>accesso ai dati</li>
              <li>rettifica</li>
              <li>cancellazione</li>
              <li>limitazione del trattamento</li>
              <li>portabilità dei dati</li>
            </ul>
            <p className="mt-4">Le richieste degli interessati devono essere gestite dal titolare del trattamento.</p>

            <h2 className="text-xl font-semibold mt-8 mb-4">10. Violazioni dei dati (Data Breach)</h2>
            <p>
              In caso di violazione dei dati personali, SmartBooker informerà il titolare del trattamento 
              senza ingiustificato ritardo fornendo tutte le informazioni necessarie per la gestione dell'evento.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">11. Durata del trattamento</h2>
            <p>Il trattamento dei dati avrà durata pari al rapporto contrattuale tra il titolare e SmartBooker.</p>
            <p className="mt-4">Alla cessazione del servizio, SmartBooker potrà:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>cancellare i dati</li>
              <li>oppure restituire i dati al titolare su richiesta</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">12. Audit e verifiche</h2>
            <p>
              Il titolare del trattamento può richiedere informazioni sulle misure di sicurezza adottate da SmartBooker.
            </p>
            <p className="mt-4">Eventuali verifiche dovranno essere concordate preventivamente.</p>

            <h2 className="text-xl font-semibold mt-8 mb-4">13. Responsabilità</h2>
            <p>
              SmartBooker è responsabile del trattamento dei dati personali solo nella misura prevista 
              dalla normativa applicabile.
            </p>
            <p className="mt-4">
              Il titolare rimane responsabile della liceità della raccolta dei dati dei propri clienti.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">14. Legge applicabile</h2>
            <p>
              Il presente accordo è disciplinato dalla legge italiana e dal Regolamento generale sulla 
              protezione dei dati.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">15. Contatti</h2>
            <p>Per qualsiasi informazione relativa al trattamento dei dati è possibile contattare:</p>
            <p className="mt-2">
              <strong>Email:</strong>{" "}
              <a href="mailto:info@smartbooker.it" className="text-accent">info@smartbooker.it</a>
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 px-4 border-t">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm text-muted-foreground">
            © 2026 SmartBooker - Powered by AleNic Service and Consulting
          </p>
        </div>
      </footer>
    </div>
  );
};

export default DataProcessingAgreement;
